import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';
import AuthorizationClient from '../single-page/api/AuthorizationClient'
import { TextMaskPhone } from '../components/TextMaskCustom';
import { TextMaskCPF } from '../components/TextMaskCustom';
import LoadingButton from '../components/LoadingButton.js'
import { trackEvent } from '../single-page/utils';

const useStyles = makeStyles((theme) => ({
  page: {
    width: '100%',
    backgroundColor: '#EFEFEF',
    borderRadius: '16px',
    padding: '24px 14px 22px',
    boxSizing: 'border-box',
  },
  title: {
    color: '#2C5E2E',
    fontWeight: 700,
    marginBottom: '6px',
    textAlign: 'center',
    fontSize: '2rem',
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.7rem',
    },
  },
  subtitle: {
    marginTop: 0,
    fontSize: '1.05rem',
    color: '#255B27',
    textAlign: 'center',
    lineHeight: 1.25,
    marginBottom: '18px',
  },
  form: {
    width: '100%',
    borderRadius: '14px',
    border: '1px solid #2A682C',
    padding: '18px 16px 16px',
    backgroundColor: '#337D36',
    boxSizing: 'border-box',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
  },
  submit: {
    margin: '14px 0 0',
    borderRadius: '8px',
    backgroundColor: '#225424',
    color: '#FFFFFF',
    fontSize: '1.05rem',
    fontWeight: 700,
    minHeight: '52px',
    '&:hover': {
      backgroundColor: '#1C441D',
    },
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  fieldLabel: {
    color: '#FFFFFF',
    fontSize: '1rem',
    marginBottom: '8px',
    marginTop: '8px',
  },
  wrapper: {
    width: '100%',
  },
  terms: {
    color: '#FFFFFF',
    fontSize: '0.95rem',
    textAlign: 'center',
    lineHeight: 1.4,
    marginTop: '16px',
    '& a': {
      color: '#89E683',
      textDecoration: 'none',
    },
  },
  input: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#FFFFFF',
      borderRadius: '8px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#D9DDE5 !important',
    },
  },
  helperText: {
    color: 'white',
    marginTop: '4px',
    minHeight: '20px',
  },
  errorAlert: {
    marginBottom: '14px',
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '0.95rem',
  },
}));

export default function SignInOrUp() {
  const classes = useStyles();
  const [user, setUser] = useState({
    phone: '',
    cpf: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [showEmail, setShowEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneMismatch, setPhoneMismatch] = useState(false);
  useEffect(() => {
    trackEvent('Lead', {}, { dedupe: false });
  }, []);

  function sign_in_or_up(e) {
    e.preventDefault();
    if (loading) return;

    if (!user.cpf.replace(/\D/g, '')) {
      setErrors({ cpf: 'CPF é obrigatório' });
      return;
    }
    if (!user.phone.replace(/\D/g, '')) {
      setErrors({ phone: 'Telefone é obrigatório' });
      return;
    }

    if (showEmail) {
      if (!user.email) {
        setErrors({ email: 'E-mail é obrigatório' });
        setLoading(false);
        return;
      }
      if (!/\S+@\S+\.\S+/.test(user.email)) {
        setErrors({ email: 'E-mail inválido' });
        setLoading(false);
        return;
      }
    }

    document.activeElement.blur();
    toast.dismiss();

    submitSignInOrUp(user);
  }

  function confirmPhoneUpdate() {
    setPhoneMismatch(false);
    submitSignInOrUp({ ...user, update_phone: true });
  }

  function submitSignInOrUp(payload) {
    AuthorizationClient.signInOrUp(payload)
      .then(function(response){
        const responsePayload = response?.data || response;
        if (responsePayload?.created) {
          trackEvent(
            'CompleteRegistration',
            {
              ...(user.email && { email: user.email }),
              phone: user.phone.replace(/\D/g, ''),
              first_name: responsePayload.name?.split(' ')[0],
              last_name: responsePayload.name?.split(' ').slice(1).join(' ')
            },
            { persistOnRedirect: true, dedupe: false }
          );
        }
        trackEvent(
          'Login',
          {
            ...(responsePayload.email && responsePayload.email !== '-' && { email: responsePayload.email }),
            phone: user.phone.replace(/\D/g, ''),
            first_name: responsePayload.name?.split(' ')[0],
            last_name: responsePayload.name?.split(' ').slice(1).join(' ')
          },
          { persistOnRedirect: true, dedupe: false }
        );
        window.location.href = '/site';
      })
      .catch(function(response){
        if (response.status == 401){
          setErrors({ cpf: response.data?.error });
        } else if (response.status == 422){
          if (response.data?.phone_mismatch) {
            setPhoneMismatch(true);
            setErrors({});
          } else if (response.data?.needs_email) {
            setShowEmail(true);
            setErrors({});
          } else if (response.data.errors && typeof response.data.errors === 'object' && !Array.isArray(response.data.errors))
            setErrors(response.data.errors)
          else
            setErrors({ cpf: response.data.errors })
        } else {
          setErrors({});
        }
      })
      .then(function() {
        setLoading(false);
      });
  }

  return (
    <div className={classes.paper}>
      <div className={classes.page}>
      <Typography className={classes.title}>
        Faça seu Login
      </Typography>
      <Typography className={classes.subtitle}>
        Acesse seu login em poucos segundos,
        <br />
        sem complicação 🚀
      </Typography>
      <form className={classes.form} onSubmit={sign_in_or_up} noValidate>
        {Object.entries(errors).map(([field, msg]) => {
          const text = Array.isArray(msg) ? msg.join(', ') : msg;
          if (!text) return null;
          return (
            <Alert key={field} severity="error" className={classes.errorAlert}>
              {text}
            </Alert>
          );
        })}
        <div className={classes.fieldLabel}>Seu CPF:</div>
        <TextField
          required={true}
          key={'cpf'}
          id={'cpf'}
          name={'cpf'}
          fullWidth={true}
          value={user['cpf'] || ''}
          autoComplete={'cpf'}
          autoFocus={true}
          error={!!errors['cpf']}
          type={'text'}
          className={classes.input}
          placeholder="Digite aqui seu CPF"
          onBlur={(e) => setUser({...user, ['cpf']: e.target.value})}
          onChange={(e) => setUser({...user, ['cpf']: e.target.value})}
          InputProps={{
            inputComponent: TextMaskCPF,
          }}
        />
        <div className={classes.fieldLabel}>Seu Celular:</div>
        <TextField
          required={true}
          key={'phone'}
          id={'phone'}
          name={'phone'}
          value={user['phone'] || ''}
          fullWidth={true}
          autoComplete={'phone'}
          error={!!(errors['phone'] || errors['unconfirmed_phone'])}
          type={'phone'}
          className={classes.input}
          placeholder="Digite aqui seu celular"
          onBlur={(e) => setUser({...user, ['phone']: e.target.value})}
          onChange={(e) => setUser({...user, ['phone']: e.target.value})}
          InputProps={{
            inputComponent: TextMaskPhone,
          }}
        />
        {showEmail && (
          <>
            <div className={classes.fieldLabel}>Seu e-mail:</div>
            <Typography style={{ color: '#89E683', fontSize: '0.88rem', marginBottom: '6px' }}>
              Cadastre seu e-mail para prosseguir
            </Typography>
            <TextField
              required={true}
              key={'email'}
              id={'email'}
              name={'email'}
              fullWidth={true}
              value={user['email'] || ''}
              autoComplete={'email'}
              autoFocus={true}
              error={!!errors['email']}
              type={'email'}
              className={classes.input}
              placeholder="Digite aqui seu e-mail"
              onBlur={(e) => setUser({...user, ['email']: e.target.value})}
              onChange={(e) => setUser({...user, ['email']: e.target.value})}
            />
          </>
        )}
        <LoadingButton
          type="submit"
          fullWidth
          loading={loading}
          variant="contained"
          color="primary"
          className={classes.submit}
        >
          Entrar
        </LoadingButton>
        <Typography className={classes.terms}>
          Ao continuar, você concorda com nossos
          <br />
          <a href='/public/termos' target='_blank' rel='noreferrer'>Termos de Uso</a> e <a href='/public/politicas' target='_blank' rel='noreferrer'>Política de Privacidade</a>.
        </Typography>
      </form>
      </div>
      <Dialog open={phoneMismatch} onClose={() => setPhoneMismatch(false)}>
        <DialogTitle>Telefone não confere</DialogTitle>
        <DialogContent>
          <DialogContentText>
            O telefone informado não confere com o cadastrado para este CPF.
            Deseja atualizar seu telefone para o informado e entrar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhoneMismatch(false)} color="default">
            Cancelar
          </Button>
          <Button onClick={confirmPhoneUpdate} color="primary" variant="contained" autoFocus>
            Atualizar e entrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
